"use client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useSearchParams, useRouter } from "next/navigation";
import { NextLink } from "@/components/ui/next-link";

import { authClient } from "@/lib/authClient";
import { useState } from "react";
import { CsLoadingScreen } from "@/components/ui/cs-loading-screen";

function translateLoginError(error: { message?: string; status?: number } | null | undefined): string {
  if (!error) return "";
  if (error.status === 401) return "E-Mail/Username oder Passwort ist falsch.";
  return "Serverfehler. Bitte versuche es später erneut.";
}

export function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") || "/home/news";
  const isBlocked = searchParams.get("blocked") === "1";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const identifier = email.trim();

    if (identifier.includes("@")) {
      const { error } = await authClient.signIn.email({
        email: identifier,
        password,
        callbackURL: callbackUrl,
        rememberMe,
      });
      if (error) {
        setLoading(false);
        setError(translateLoginError(error));
      }
      return;
    }

    // The username endpoint has no callbackURL redirect support, so navigate manually.
    const { error } = await authClient.signIn.username({
      username: identifier,
      password,
      rememberMe,
    });
    if (error) {
      setLoading(false);
      setError(translateLoginError(error));
      return;
    }
    router.refresh();
    router.push(callbackUrl);
  }

  return (
    <>
      {loading && <CsLoadingScreen text="Connecting to server..." />}
      {isBlocked && <div className="mb-4 rounded-md border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-400">Dein Konto wurde gesperrt. Bitte wende dich an einen Administrator.</div>}
      <form className={cn("flex flex-col gap-6")} onSubmit={handleSubmit} autoComplete="on">
        <div className="grid gap-6">
          <div className="grid gap-2">
            <Label htmlFor="email">Email oder Username</Label>
            <Input id="email" type="text" name="email" autoComplete="username" placeholder="dracherw@bk-lan.de" value={email} required onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="password">Passwort</Label>
              <NextLink href="/password-reset" tabIndex={-1} className="ml-auto text-sm underline-offset-4 hover:underline">
                Passwort vergessen?
              </NextLink>
            </div>
            <Input id="password" type="password" name="password" autoComplete="current-password" value={password} required onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="rememberMe" checked={rememberMe} onCheckedChange={(v) => setRememberMe(!!v)} />
            <Label htmlFor="rememberMe" className="font-normal cursor-pointer">
              Angemeldet bleiben
            </Label>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full">
            Login
          </Button>
        </div>
        <div className="text-center text-sm">
          Noch keinen Account? <br />
          <a href="/signup" className="underline underline-offset-4">
            Jetzt registrieren!
          </a>
        </div>
      </form>
    </>
  );
}
